import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { useUser } from '../context/UserContext';
import './LandingScreen.css';

const LandingScreen = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    // Se o usuário já estiver logado, redireciona para a home
    if (user) {
      navigate('/home');
    }

    // Carrega o script da API do Vimeo
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Limpeza ao desmontar o componente
      document.body.removeChild(script);
    };
  }, [user, navigate]);

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      await signInWithEmailAndPassword(auth, loginData.email, loginData.password);
      // O redirecionamento será tratado pelo useEffect que monitora o usuário
    } catch (error) {
      console.error('Erro no login:', error);
      setLoginError(getLoginErrorMessage(error.code));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoginLoading(true);
    setLoginError('');

    try {
      await signInWithPopup(auth, googleProvider);
      // O redirecionamento será tratado pelo useEffect que monitora o usuário
    } catch (error) {
      console.error('Erro no login com Google:', error);
      setLoginError(getLoginErrorMessage(error.code));
    } finally {
      setLoginLoading(false);
    }
  };

  const getLoginErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'E-mail inválido. Verifique o formato.';
      case 'auth/user-disabled':
        return 'Esta conta foi desativada. Contate o suporte.';
      case 'auth/user-not-found':
        return 'E-mail não encontrado. Verifique ou crie uma conta.';
      case 'auth/wrong-password':
        return 'Senha incorreta. Tente novamente.';
      case 'auth/popup-closed-by-user':
        return 'Login com Google cancelado.';
      default:
        return 'Ocorreu um erro durante o login. Tente novamente.';
    }
  };

  return (
    <div className="landing-container">
      {/* Cabeçalho com opções de login */}
      <header className="landing-header">
        <div className="header-content">
          <div className="logo-container">
            <img 
              src="/logo-lotopim-1.jpeg" 
              alt="LotoPIM Logo" 
              className="logo"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyNSIgY3k9IjI1IiByPSIyNCIgZmlsbD0iIzY2N2VlYSIvPjxwYXRoIGQ9Ik0xOCAyOEwyNSAzNUwzMiAyNU0yNSAxNVYzNSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48L3N2Zz4=';
              }}
            />
            <span className="logo-text">LotoPIM</span>
          </div>

          <div className="auth-actions">
            <button 
              className="auth-toggle-btn"
              onClick={() => setShowLogin(!showLogin)}
            >
              <i className={`fas ${showLogin ? 'fa-times' : 'fa-user'} me-2`}></i>
              {showLogin ? 'Fechar' : 'Acessar Conta'}
            </button>
          </div>
        </div>

        {/* Painel de login dropdown */}
        {showLogin && (
          <div className="login-panel">
            <div className="login-header">
              <h3>Acesse sua conta</h3>
              <p>Entre para gerenciar seus jogos</p>
            </div>

            {loginError && (
              <div className="login-error">
                <i className="fas fa-exclamation-circle me-2"></i>
                {loginError}
              </div>
            )}

            <form onSubmit={handleEmailLogin} className="login-form">
              <div className="input-group">
                <label htmlFor="login-email">
                  <i className="fas fa-envelope me-2"></i>
                  E-mail
                </label>
                <input
                  type="email"
                  id="login-email"
                  name="email"
                  value={loginData.email}
                  onChange={handleLoginChange}
                  placeholder="seu@email.com"
                  disabled={loginLoading}
                  required
                />
              </div>

              <div className="input-group">
                <label htmlFor="login-password">
                  <i className="fas fa-lock me-2"></i>
                  Senha
                </label>
                <input
                  type="password"
                  id="login-password"
                  name="password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  placeholder="Sua senha"
                  disabled={loginLoading}
                  required
                />
              </div>

              <button 
                type="submit" 
                className="login-btn"
                disabled={loginLoading}
              >
                {loginLoading ? (
                  <>
                    <span className="spinner"></span>
                    Autenticando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt me-2"></i>
                    Autenticar
                  </>
                )}
              </button>
            </form>

            <div className="login-divider">
              <span>ou</span>
            </div>

            <button 
              onClick={handleGoogleLogin}
              className="google-login-btn"
              disabled={loginLoading}
            >
              <img 
                src="https://developers.google.com/identity/images/g-logo.png" 
                alt="Google Logo" 
                className="google-logo"
              />
              Entrar com Google
            </button>

            <div className="login-footer">
              <p>
                Não tem uma conta? <Link to="/signup">Cadastre-se agora</Link>
              </p>
            </div>
          </div>
        )}
      </header>

      {/* Vídeo como plano de fundo responsivo */}
      <div className="video-background">
        <iframe 
          src="https://player.vimeo.com/video/1076722551?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;autoplay=1&amp;loop=1&amp;muted=1&amp;background=1" 
          style={{ border: 'none' }}
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
          referrerPolicy="strict-origin-when-cross-origin" 
          title="vomos_site_intro"
        ></iframe>
      </div>
      
      <div className="content-overlay">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-8 text-center">
              <h1 className="display-4 text-white mb-4">Bem-vindo ao Lotopim</h1>
              <p className="lead text-white mb-5">
                Transforme sua sorte com nossas estratégias inteligentes para jogos de loteria
              </p>
              
              {/* Call to Action adicional para incentivar o registro */}
              <div className="cta-section">
                <p className="text-light mb-3">Acesse agora e descubra como aumentar suas chances de ganhar!</p>
                <button 
                  className="cta-button"
                  onClick={() => setShowLogin(true)}
                >
                  <i className="fas fa-star me-2"></i>
                  Comece Agora
                </button>
              </div>
              
              <div className="features mt-5">
                <div className="row">
                  <div className="col-md-4">
                    <div className="feature-item">
                      <i className="fas fa-chart-line fa-3x text-warning mb-3"></i>
                      <h5 className="text-white">Estratégias Avançadas</h5>
                      <p className="text-light">Algoritmos inteligentes para aumentar suas chances</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="feature-item">
                      <i className="fas fa-lock fa-3x text-warning mb-3"></i>
                      <h5 className="text-white">Segurança</h5>
                      <p className="text-light">Seus dados protegidos com criptografia</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="feature-item">
                      <i className="fas fa-trophy fa-3x text-warning mb-3"></i>
                      <h5 className="text-white">Resultados Comprovados</h5>
                      <p className="text-light">Jogadores satisfeitos em todo o Brasil</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rodapé com informações corporativas */}
      <footer className="landing-footer">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-4">
              <img 
                src="/logo-lotopim-1.jpeg" 
                alt="LotoPIM Logo" 
                className="footer-logo"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyNSIgcyBjeT0iMjUiIHI9IjI0IiBmaWxsPSIjNjY3ZWVhIi8+PHBhdGggZD0iTTE4IDI4TDI1IDM1TDMyIDI1TTI1IDE1VjM1IiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==';
                }}
              />
            </div>
            <div className="col-md-8 text-md-end">
              <p className="company-info">
                Powered By CHF Serviços em Tecnologia
                <br />
                Todos os Direitos Reservados
                <br />
                CNPJ: 11.338.930/0001-76
              </p>
              <div className="footer-links">
                <a href="/terms">Termos de Uso</a>
                <a href="/privacy">Política de Privacidade</a>
                <a href="/contact">Contato</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingScreen;